/**
 * iter87.199.190 — RivalryLinkCopyCard.
 *
 * Sits right below InviteTextCopyCard on the Rivalry → Messages tab.
 * For crewmates who are ALREADY REGISTERED — a plain direct link they
 * can click straight back into their rivalry (their browser's saved
 * session logs them in automatically). No invite code, no setup copy.
 *
 * Note: the app doesn't currently support deep-linking to one SPECIFIC
 * tournament within a multi-tournament rivalry via URL — a returning
 * crewmate always lands on the rivalry's default tournament (or their
 * own last-viewed one, if their browser remembers it). This link is
 * scoped at the rivalry level for that reason.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, Check } from "lucide-react";

export default function RivalryLinkCopyCard({ leagueId }) {
    const [copied, setCopied] = useState(false);
    const url = `${window.location.origin}/league/${leagueId}`;

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Link copied");
        } catch {
            toast.error("Clipboard access blocked by your browser");
        }
    };

    if (!leagueId) return null;

    return (
        <div className="brutal-card-light p-4 space-y-3" data-testid="rivalry-link-copy-card">
            <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-cyber flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Direct link for registered crewmates
                </h3>
                <p className="text-xs text-zinc-400 mt-1">
                    For crewmates already in the rivalry — no code needed, their session logs them straight back in.
                </p>
            </div>
            <pre
                className="whitespace-pre-wrap break-all bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white font-mono leading-relaxed"
                data-testid="rivalry-link-copy-body"
            >
                {url}
            </pre>
            <button
                onClick={doCopy}
                className="brutal-btn-primary flex items-center gap-1.5 text-xs px-3 py-2 w-full justify-center"
                data-testid="rivalry-link-copy-btn"
            >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy URL"}
            </button>
        </div>
    );
}
